import { Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useSampleSet } from '../../../../../../contexts/SampleSetContext';
import { useUser } from '../../../../../../contexts/UserContext';
import {
  FEATURE_FLAGS,
  useFeatureFlag,
} from '../../../../../../lib/featureFlags';
import ReportsTab from '../../../../../../pages/experiments/ReportsTab';
import { sampleSetInterpretiveReportsQueryOptions } from '../../../../../../services/queries';

export const Route = createFileRoute(
  '/_authenticated/_navbar/experiments/$sampleSetId/reports/'
)({
  component: ReportsIndexRoute,
});

function ReportsIndexRoute() {
  const { sampleSet } = useSampleSet();
  const { hasPermission } = useUser();
  const compactHeader = useFeatureFlag(FEATURE_FLAGS.compactHeader);

  // Only needed for the compact flow's auto-open.
  const { data, isLoading } = useQuery({
    ...sampleSetInterpretiveReportsQueryOptions(
      sampleSet?.id ?? '',
      hasPermission
    ),
    enabled: compactHeader && !!sampleSet?.id,
  });

  if (!sampleSet) {
    return null;
  }

  // Legacy: the card list, each card opening the separate report page.
  if (!compactHeader) {
    return <ReportsTab sampleSetId={sampleSet.id} />;
  }

  // Compact: land straight on the newest report; the header's selector handles switching.
  if (isLoading) {
    return null;
  }

  const newestReport = [...(data?.data ?? [])].sort(
    (a, b) =>
      new Date(b.created_date).getTime() - new Date(a.created_date).getTime()
  )[0];

  if (!newestReport) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          No reports for this sample set yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Navigate
      to="/experiments/$sampleSetId/reports/$reportId"
      params={{ sampleSetId: sampleSet.id, reportId: newestReport.id }}
      search={true}
      replace
    />
  );
}
